/* iter189 — Inbound SMS Replies tab.
   Simple admin viewer for customer replies captured by /api/twilio/inbound-sms.
   Filters + mark-reviewed + auto-reply settings + copy-webhook-URL helper.
*/
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Inbox, RefreshCw, Copy, CheckCircle2, MessageSquare, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDT = (v) => {
  if (!v) return '';
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
};

const InboundSmsTab = () => {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [reviewedFilter, setReviewedFilter] = useState('no');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    auto_reply_enabled: false,
    auto_reply_text: '',
    cooldown_hours: 4,
    notify_office_sms: true,
  });

  const webhookUrl = `${BACKEND_URL}/api/twilio/inbound-sms`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ reviewed: reviewedFilter });
      if (q) params.set('q', q);
      const { data } = await axios.get(`${API}/admin/sms/inbound?${params}`, { withCredentials: true });
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load replies');
    }
    setLoading(false);
  }, [reviewedFilter, q]);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/sms/inbound/settings`, { withCredentials: true });
      setSettings(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { load(); loadSettings(); }, [load, loadSettings]);

  const markReviewed = async (id, reviewed = true) => {
    try {
      await axios.put(`${API}/admin/sms/inbound/${id}/reviewed?reviewed=${reviewed}`, {}, { withCredentials: true });
      toast.success(reviewed ? 'Marked reviewed' : 'Marked unreviewed');
      load();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const saveSettings = async () => {
    try {
      await axios.put(`${API}/admin/sms/inbound/settings`, settings, { withCredentials: true });
      toast.success('Auto-reply settings saved');
      loadSettings();
    } catch (e) {
      toast.error('Save failed');
    }
  };

  const copy = (v) => {
    if (!v) return;
    navigator.clipboard.writeText(v).then(() => toast.success('Copied'));
  };

  return (
    <div className="space-y-6" data-testid="inbound-sms-tab">
      {/* Twilio webhook URL banner */}
      <div className="border border-[#D4AF37]/40 rounded-lg bg-[#D4AF37]/5 p-4">
        <div className="flex items-start gap-3">
          <ExternalLink className="w-5 h-5 text-[#D4AF37] mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="text-white font-semibold text-sm">Twilio inbound webhook URL</div>
            <div className="text-[11px] text-[#A3A3A3] mt-1">
              In Twilio Console → Phone Numbers → your number → Messaging → &quot;A message comes in&quot; →
              set to <span className="text-white">HTTP POST</span> pointing at the URL below.
            </div>
            <div className="mt-2 flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded px-2 py-1.5">
              <code data-testid="webhook-url" className="text-[#D4AF37] text-xs font-mono break-all flex-1">{webhookUrl}</code>
              <Button data-testid="copy-webhook" size="sm" variant="ghost" onClick={() => copy(webhookUrl)}>
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Auto-reply settings */}
      <div className="border border-[#262626] rounded-lg bg-[#0d0d0d] p-4">
        <div className="text-white font-semibold text-sm mb-3">Automatic reply to inbound texts</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex items-center gap-2 text-sm text-[#D4D4D4]">
            <input
              type="checkbox"
              data-testid="auto-reply-enabled"
              checked={settings.auto_reply_enabled}
              onChange={(e) => setSettings((s) => ({ ...s, auto_reply_enabled: e.target.checked }))}
              className="w-4 h-4"
            />
            Enable auto-reply when a customer texts back
          </label>
          <label className="flex items-center gap-2 text-sm text-[#D4D4D4]">
            <input
              type="checkbox"
              data-testid="notify-office-sms"
              checked={settings.notify_office_sms}
              onChange={(e) => setSettings((s) => ({ ...s, notify_office_sms: e.target.checked }))}
              className="w-4 h-4"
            />
            Also SMS Dispatch on every inbound reply
          </label>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Cooldown (hours)</label>
            <Input
              data-testid="auto-reply-cooldown"
              type="number" min={0} max={168}
              value={settings.cooldown_hours}
              onChange={(e) => setSettings((s) => ({ ...s, cooldown_hours: Number(e.target.value) || 0 }))}
            />
            <p className="text-[10px] text-[#525252] mt-1">
              Won&apos;t auto-reply more than once to the same phone within this window.
            </p>
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Auto-reply text</label>
            <Textarea
              data-testid="auto-reply-text"
              value={settings.auto_reply_text}
              onChange={(e) => setSettings((s) => ({ ...s, auto_reply_text: e.target.value }))}
              placeholder="Thanks for your message. This inbox is monitored by the NexFlow office team. For faster help, call or text 614-285-4502. Reply STOP to opt out."
              className="h-20"
            />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button data-testid="save-inbound-settings" onClick={saveSettings} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold">
            Save Settings
          </Button>
        </div>
        <p className="text-[10px] text-[#525252] mt-2">
          STOP / HELP / START / UNSUBSCRIBE replies are never auto-replied to (carrier compliance).
          Auto-reply also never fires to internal employee numbers.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Search</label>
          <Input
            data-testid="inbound-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Phone, customer name, or message text…"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Status</label>
          <Select value={reviewedFilter} onValueChange={setReviewedFilter}>
            <SelectTrigger data-testid="inbound-reviewed-filter" className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="no">Unreviewed</SelectItem>
              <SelectItem value="yes">Reviewed</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="inbound-refresh" onClick={load} disabled={loading} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <div className="text-sm text-[#A3A3A3] pb-2">
          <span className="text-white font-semibold" data-testid="inbound-count">{count}</span> replies
        </div>
      </div>

      {/* List */}
      <div className="space-y-2" data-testid="inbound-list">
        {loading && items.length === 0 ? (
          <div className="text-center text-[#A3A3A3] py-8">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 border border-[#262626] rounded bg-[#0d0d0d]" data-testid="inbound-empty">
            <Inbox className="w-8 h-8 text-[#525252] mx-auto mb-2" />
            <div className="text-[#A3A3A3] text-sm">No inbound replies match this filter.</div>
          </div>
        ) : items.map((it) => (
          <div key={it.id} className="border border-[#262626] rounded bg-[#0d0d0d] p-3" data-testid={`inbound-${it.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <MessageSquare className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-white font-semibold text-sm">
                    {(it.matched_customer?.name) || it.from}
                  </span>
                  <span className="text-[11px] text-[#A3A3A3] font-mono">{it.from}</span>
                  {it.matched_customer && (
                    <Badge className="text-[10px] bg-blue-500/15 border-blue-500/40 text-blue-200">Customer matched</Badge>
                  )}
                  <span className="text-[11px] text-[#525252]">{fmtDT(it.created_at)}</span>
                </div>
                <div className="mt-1.5 text-[#E5E5E5] text-sm whitespace-pre-wrap">{it.body}</div>
                <div className="mt-1.5 flex items-center gap-3 text-[10px] text-[#525252] flex-wrap">
                  {it.matched_outbound?.event && <span>Reply to <span className="text-[#A3A3A3]">{it.matched_outbound.event}</span></span>}
                  {it.auto_reply_status && (
                    <span>Auto-reply: <span className={
                      it.auto_reply_status === 'sent' ? 'text-green-300' :
                      it.auto_reply_status === 'skipped' ? 'text-amber-200' : 'text-[#A3A3A3]'}>
                      {it.auto_reply_status}{it.auto_reply_reason ? ` (${it.auto_reply_reason})` : ''}
                    </span></span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!it.reviewed ? (
                  <Button size="sm" onClick={() => markReviewed(it.id, true)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]" data-testid={`inbound-mark-reviewed-${it.id}`}>
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Reviewed
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => markReviewed(it.id, false)} data-testid={`inbound-unmark-${it.id}`}>
                    Reviewed · Undo
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InboundSmsTab;
